import { AppUser, getV2ValueWP, tryParseJSON } from "@eventr/common";
import { ColoursObj, getContrastYIQ, Typography } from "@eventr/common-storybook";
import { Box, Stack } from "@mui/material";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { DirectChatButton } from "home/components/DirectChatButton";
import { useEffect, useState } from "react";
import { routes } from "routing";
import { IUserWithTag } from "../../data/Data";
import { MemberProfilePic } from "../../home/team/components/MemberProfilePic";
import { useBoundStore } from "../../state/bound.store";
import { IOptionsWithTypes } from "../../state/interfaces";

import { useShallow } from "zustand/react/shallow";
import { useFormatValue } from "../hooks/useFormatValue";
import { UserListText } from "./userListText";
interface IUsersListProps {
  id?: string;
  data: any;
  renderOptions: IUsersListOptions;
  past?: boolean;
}
interface IUsersListOptions {
  users: string;
  keyInArray?: string;
  bookingSpecific?: boolean;
  primary?: string;
  secondary?: string;
  formatter?: string;
  textOutput?: boolean;
  hideChat?: boolean;
}
export const UsersList = ({ id, data, renderOptions, past }: IUsersListProps) => {
  const { pushPath } = useGlobalRouter();
  const {
    getUserFromID,
    getUsersWithTags,
    getCurrentUser,
    getNameFormatted,
    selectedItineraryUsers,
    selectedItineraryUsersOnlyFromGroupsAndDepartments,
  } = useBoundStore(
    useShallow((s) => {
      return {
        getUserFromID: s.getUserFromID,
        getUsersWithTags: s.getUsersWithTags,
        getCurrentUser: s.getCurrentUser,
        getNameFormatted: s.getNameFormatted,
        selectedItineraryUsers: s.selectedItineraryUsers,
        selectedItineraryUsersOnlyFromGroupsAndDepartments: s.selectedItineraryUsersOnlyFromGroupsAndDepartments,
      };
    }),
  );
  const { formatValue } = useFormatValue();

  const [users, setUsers] = useState<AppUser[]>([]);
  const currentUser = getCurrentUser();

  useEffect(() => {
    const usersWithTagsMaybe = tryParseJSON(getV2ValueWP(renderOptions.users, data, currentUser), []);

    /// To filter for only users in the itinerary. (so doesnt show removed users)
    const itinUserIds = [
      ...selectedItineraryUsers().map((user) => {
        return user.id;
      }),
      ...selectedItineraryUsersOnlyFromGroupsAndDepartments().map((user) => {
        return user.id;
      }),
    ];

    const usersWithTags: IOptionsWithTypes[] = [];
    if (renderOptions.keyInArray) {
      usersWithTagsMaybe.forEach((u: any) => {
        if (renderOptions.keyInArray && Array.isArray(u[renderOptions.keyInArray])) {
          if (renderOptions.bookingSpecific) {
            const bookignWithId = u[renderOptions.keyInArray].some((userWithID: any) => {
              return userWithID.option === currentUser?.id && data.identifier === u._id;
            });

            if (bookignWithId) {
              usersWithTags.push(...u[renderOptions.keyInArray]);
            }
          } else {
            usersWithTags.push(...u[renderOptions.keyInArray]);
          }
        } else {
          if (renderOptions.keyInArray) {
            usersWithTags.push(u[renderOptions.keyInArray]);
          }
        }
      });
    } else {
      usersWithTags.push(...usersWithTagsMaybe);
    }

    const _users: AppUser[] = [];
    const tagsDep: string[] = [];
    const tagsGroup: string[] = [];

    usersWithTags.forEach((u) => {
      if (u && u.type === "User") {
        const user = getUserFromID(Number.parseInt(u.option?.toString()));

        const inList = _users?.some((u) => {
          return u?.id === user?.id;
        });
        if (user && !inList) {
          if (renderOptions.bookingSpecific) {
            if (itinUserIds.includes(user.id)) _users.push(user);
          } else {
            _users.push(user);
          }
        }
      }
      if (u && u.type === "Department") {
        tagsDep.push(u.option?.toString());
      }
      if (u && u.type === "Group") {
        tagsGroup.push(u.option?.toString());
      }
    });
    const tagUsers = getUsersWithTags(tagsDep, tagsGroup);
    // only add users that are not already in the list
    tagUsers.forEach((u) => {
      if (!_users?.find((user) => user.id === u.id)) {
        _users.push(u);
      }
    });

    setUsers(_users);
  }, [
    data,
    getUserFromID,
    getUsersWithTags,
    renderOptions.users,
    renderOptions.keyInArray,
    selectedItineraryUsers,
    selectedItineraryUsersOnlyFromGroupsAndDepartments,
    currentUser,
    renderOptions.bookingSpecific,
  ]);

  const getUserSubInfo = (userId: number) => {
    const bookings = data[renderOptions.users];
    const userBookings = bookings
      .filter((booking: any) => {
        return (
          renderOptions.keyInArray &&
          (booking[renderOptions.keyInArray] || []).some((user: IUserWithTag) => {
            return user.option === userId;
          })
        );
      })
      .sort((a: any, b: any) => {
        return renderOptions.primary && a[renderOptions.primary] > b[renderOptions.primary] ? 1 : -1 ? 1 : -1;
      });

    return (
      <Stack>
        {userBookings.map((booking: any) => {
          return (
            <div key={booking._id + "subInfo"}>
              <Typography
                id="startDateText"
                variant="bodySubtext"
                color={getContrastYIQ(ColoursObj.backgrounds.secondary)}
              >
                {formatValue(booking[renderOptions.primary ?? ""], renderOptions.formatter ?? "")}
              </Typography>
              {booking[renderOptions.secondary ?? ""] && (
                <Typography
                  id="endDateText"
                  variant="bodySubtext"
                  color={getContrastYIQ(ColoursObj.backgrounds.secondary)}
                >
                  {" - " + formatValue(booking[renderOptions.secondary ?? ""], renderOptions.formatter ?? "")}
                </Typography>
              )}
            </div>
          );
        })}
      </Stack>
    );
  };
  if (renderOptions.textOutput) {
    return <UserListText users={users} />;
  }

  return (
    <Stack spacing="8px" sx={{
      marginTop: "8px"
    }}>
      {users.map((user, rowId) => (
        <Stack
          key={rowId}
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Stack
            spacing="16px"
            direction="row"
            onClick={() => pushPath(routes.team.member.build({ userId: user.id }))}
            sx={{
              alignItems: "center"
            }}
          >
            <MemberProfilePic user={user} />
            <Box>
              <Typography
                id={id + "userNames"}
                variant="bodyRegular"
                color={past ? ColoursObj.shades.shade2 : ColoursObj.font.primary}
                sx={{
                  width: "calc(var(--app-width) * 0.56)",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                {getNameFormatted(user)}
              </Typography>
              {renderOptions.primary && <>{getUserSubInfo(user?.id)}</>}
            </Box>
          </Stack>

          {user.firstName && user.lastName && renderOptions.hideChat !== true && (
            <DirectChatButton user_id={currentUser?.id ?? -1} user_id2={user?.id} fullWidth={false} past={past} />
          )}
        </Stack>
      ))}
    </Stack>
  );
};
